<template>
    <v-card flat class="comp-allegati">
        <!-- Header sticky coerente -->
        <v-card-title class="comp-header pa-4 pb-2">
            <v-icon size="18" class="text-medium-emphasis me-2">mdi-paperclip</v-icon>
            <span class="text-h6 font-weight-bold text-truncate">Allegati</span>
            <v-chip v-if="allegatiInterni.length > 0" size="x-small" color="primary" variant="tonal" class="me-2 ml-3">
                {{ allegatiInterni.length }}
            </v-chip>
        </v-card-title>

        <v-card-text class="pa-3 pt-2">

            <!-- ─── Pulsanti azione ─── -->
            <div class="action-buttons mb-3">
                <!-- Upload file -->
                <div class="upload-zone" @click="triggerFileInput" @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" :class="{ 'drag-over': isDragOver }">
                    <input ref="fileInput" type="file" multiple class="d-none" @change="onFileChange" />
                    <v-icon size="28" class="upload-icon mb-1">mdi-cloud-upload-outline</v-icon>
                    <p class="text-body-2 text-medium-emphasis ma-0">
                        <span class="text-primary font-weight-semibold">Clicca qui</span> o trascina i file
                    </p>
                    <p class="text-caption text-grey ma-0 mt-1">PDF, immagini, documenti</p>
                </div>

                <!-- Scatta foto -->
                <div class="camera-zone" @click="apriCamera" :class="{ 'camera-active': cameraAperta }">
                    <v-icon size="28" class="camera-icon mb-1">mdi-camera</v-icon>
                    <p class="text-body-2 text-medium-emphasis ma-0">
                        <span class="camera-label font-weight-semibold">Scatta foto</span>
                    </p>
                    <p class="text-caption text-grey ma-0 mt-1">JPG automatico</p>
                </div>
            </div>

            <!-- ─── Modale fotocamera ─── -->
            <v-dialog v-model="cameraAperta" max-width="640" persistent>
                <v-card class="camera-dialog">
                    <v-card-title class="d-flex align-center pa-3 pb-2">
                        <v-icon class="me-2" color="primary">mdi-camera</v-icon>
                        <span class="text-h6">Scatta una foto</span>
                        <v-spacer />
                        <v-btn icon size="small" variant="text" @click="chiudiCamera">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>

                    <v-divider />

                    <v-card-text class="pa-3">
                        <!-- Anteprima video live -->
                        <div v-if="!fotoAcquisita" class="video-container">
                            <video ref="videoEl" autoplay playsinline muted class="camera-video" />
                            <!-- Mirino overlay -->
                            <div class="viewfinder-overlay">
                                <div class="corner tl" />
                                <div class="corner tr" />
                                <div class="corner bl" />
                                <div class="corner br" />
                            </div>
                            <!-- Errore fotocamera -->
                            <div v-if="cameraError" class="camera-error-overlay">
                                <v-icon size="48" color="error">mdi-camera-off</v-icon>
                                <p class="text-body-2 mt-2">{{ cameraError }}</p>
                            </div>
                        </div>

                        <!-- Anteprima foto scattata -->
                        <div v-else class="preview-container">
                            <img :src="fotoDataUrl" class="foto-preview" alt="Foto acquisita" />
                            <div class="preview-label">
                                <v-icon size="14" class="me-1">mdi-check-circle</v-icon>
                                {{ nomeFileFoto }}
                            </div>
                        </div>

                        <!-- Canvas nascosto per cattura -->
                        <canvas ref="canvasEl" class="d-none" />
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="pa-3 gap-2">
                        <!-- Seleziona fotocamera (se multipla) -->
                        <v-select v-if="!fotoAcquisita && dispositivi.length > 1" v-model="dispositivoSelezionato"
                            :items="dispositivi" item-title="label" item-value="deviceId" density="compact"
                            variant="outlined" hide-details class="camera-select" @update:model-value="cambiaCamera"
                            style="max-width: 200px;" />

                        <v-spacer />

                        <template v-if="!fotoAcquisita">
                            <v-btn variant="text" color="grey" @click="chiudiCamera">Annulla</v-btn>
                            <v-btn color="primary" variant="elevated" :disabled="!!cameraError || !streamAttivo"
                                @click="scattaFoto" prepend-icon="mdi-camera">
                                Scatta
                            </v-btn>
                        </template>

                        <template v-else>
                            <v-btn variant="text" color="grey" @click="riscatta" prepend-icon="mdi-camera-retake">
                                Riscatta
                            </v-btn>
                            <v-btn color="success" variant="elevated" :loading="loading" @click="confermaFoto"
                                prepend-icon="mdi-upload">
                                Carica
                            </v-btn>
                        </template>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Loading bar durante upload -->
            <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-2 mb-1" />

            <!-- ─── Lista allegati ─── -->
            <v-list v-if="allegatiInterni.length > 0" class="allegati-list pa-0 mt-2">
                <v-list-item v-for="allegato in allegatiInterni" :key="allegato.id" density="compact"
                    class="allegato-item">
                    <template v-slot:prepend>
                        <v-avatar size="32" :color="iconColor(allegato.nomeFile)" class="avatar-file">
                            <v-icon size="16" color="white">{{ iconFile(allegato.nomeFile) }}</v-icon>
                        </v-avatar>
                    </template>
                    <v-list-item-title class="text-body-2 text-truncate">{{ allegato.nomeFile }}</v-list-item-title>
                    <v-list-item-subtitle v-if="allegato.dimensione" class="text-caption text-grey">
                        {{ formatSize(allegato.dimensione) }}
                    </v-list-item-subtitle>
                    <template v-slot:append>
                        <div class="item-actions">
                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                @click="scaricaAllegato(allegato)" aria-label="Scarica">
                                <v-icon size="16">mdi-download</v-icon>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="error" @click="eliminaAllegato(allegato)"
                                aria-label="Elimina">
                                <v-icon size="16">mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </v-list-item>
            </v-list>

            <p v-else class="text-caption text-grey text-center mt-3 mb-0">
                Nessun allegato caricato
            </p>
        </v-card-text>
    </v-card>
</template>

<script>
import { ref, watch, nextTick } from 'vue'
import { carica_allegato, scarica_allegato, elimina_allegato, elenco_allegati } from '@/api/api.misure.js'

export default {
    name: 'GestioneAllegati',

    props: {
        ordineId: { type: [String, Number], required: true },
        allegati: { type: Array, default: () => [] }
    },

    emits: ['allegati-aggiornati'],

    setup(props, { emit }) {
        const loading = ref(false)
        const fileInput = ref(null)
        const allegatiInterni = ref([...props.allegati])
        const isDragOver = ref(false)

        // ── Camera refs ──
        const cameraAperta = ref(false)
        const videoEl = ref(null)
        const canvasEl = ref(null)
        const fotoAcquisita = ref(false)
        const fotoDataUrl = ref(null)
        const nomeFileFoto = ref('')
        const cameraError = ref(null)
        const streamAttivo = ref(false)
        const streamCorrente = ref(null)
        const dispositivi = ref([])
        const dispositivoSelezionato = ref(null)

        watch(() => props.allegati, (newVal) => {
            allegatiInterni.value = [...newVal]
        }, { deep: true })

        // ── Upload file ──
        const triggerFileInput = () => fileInput.value?.click()

        const onFileChange = (e) => {
            const files = e.target.files
            if (files?.length) caricaFiles(Array.from(files))
            e.target.value = ''
        }

        const onDragOver = () => { isDragOver.value = true }
        const onDragLeave = () => { isDragOver.value = false }

        const onDrop = (e) => {
            isDragOver.value = false
            const files = e.dataTransfer.files
            if (files?.length) caricaFiles(Array.from(files))
        }

        const caricaFiles = async (files) => {
            loading.value = true
            try {
                for (const file of files) {
                    await carica_allegato(props.ordineId, file)
                }
                let response = await elenco_allegati(props.ordineId);
                allegatiInterni.value = response.data;
                emit('allegati-aggiornati', allegatiInterni.value)
            } catch (error) {
                console.error('Errore caricamento allegati:', error)
            }
            loading.value = false
        }

        // ── Camera ──

        /** Genera nome file univoco tipo: foto_20250327_143022.jpg */
        const generaNomeFoto = () => {
            const now = new Date()
            const pad = (n) => String(n).padStart(2, '0')
            const data = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
            const ora = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
            return `foto_${data}_${ora}.jpg`
        }

        /** Recupera la lista dei dispositivi video disponibili */
        const enumeraDispositivi = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                const videoDevices = devices
                    .filter(d => d.kind === 'videoinput')
                    .map((d, i) => ({
                        deviceId: d.deviceId,
                        label: d.label || `Fotocamera ${i + 1}`
                    }))
                dispositivi.value = videoDevices
                // Prova a selezionare la fotocamera posteriore su tablet
                const posteriore = videoDevices.find(d =>
                    /back|rear|environment/i.test(d.label)
                )
                dispositivoSelezionato.value = posteriore?.deviceId || videoDevices[0]?.deviceId || null
            } catch {
                dispositivi.value = []
            }
        }

        /** Avvia lo stream video */
        const avviaStream = async (deviceId = null) => {
            // Ferma eventuale stream precedente
            fermaStream()
            cameraError.value = null
            streamAttivo.value = false

            const constraints = {
                video: deviceId
                    ? { deviceId: { exact: deviceId } }
                    : { facingMode: { ideal: 'environment' } }, // posteriore preferita
                audio: false
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints)
                streamCorrente.value = stream
                streamAttivo.value = true

                // Assegna allo <video> appena il DOM è pronto
                await nextTick()
                if (videoEl.value) {
                    videoEl.value.srcObject = stream
                }
            } catch (err) {
                console.error('Errore accesso fotocamera:', err)
                if (err.name === 'NotAllowedError') {
                    cameraError.value = 'Permesso fotocamera negato. Autorizza l\'accesso nelle impostazioni.'
                } else if (err.name === 'NotFoundError') {
                    cameraError.value = 'Nessuna fotocamera trovata sul dispositivo.'
                } else {
                    cameraError.value = 'Impossibile accedere alla fotocamera.'
                }
            }
        }

        const fermaStream = () => {
            if (streamCorrente.value) {
                streamCorrente.value.getTracks().forEach(t => t.stop())
                streamCorrente.value = null
            }
            streamAttivo.value = false
            if (videoEl.value) videoEl.value.srcObject = null
        }

        const apriCamera = async () => {
            fotoAcquisita.value = false
            fotoDataUrl.value = null
            nomeFileFoto.value = ''
            cameraError.value = null
            cameraAperta.value = true
            await enumeraDispositivi()
            await avviaStream(dispositivoSelezionato.value)
        }

        const chiudiCamera = () => {
            fermaStream()
            cameraAperta.value = false
            fotoAcquisita.value = false
            fotoDataUrl.value = null
        }

        const cambiaCamera = async (deviceId) => {
            await avviaStream(deviceId)
        }

        /** Cattura il frame corrente dal video e lo converte in JPG */
        const scattaFoto = () => {
            if (!videoEl.value || !canvasEl.value) return

            const video = videoEl.value
            const canvas = canvasEl.value
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight

            const ctx = canvas.getContext('2d')
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

            // Converti sempre in JPG con qualità 0.92
            fotoDataUrl.value = canvas.toDataURL('image/jpeg', 0.92)
            nomeFileFoto.value = generaNomeFoto()
            fotoAcquisita.value = true

            // Ferma lo stream per risparmiare batteria
            fermaStream()
        }

        const riscatta = async () => {
            fotoAcquisita.value = false
            fotoDataUrl.value = null
            await avviaStream(dispositivoSelezionato.value)
        }

        /** Converte dataUrl in File e lo carica tramite l'API esistente */
        const confermaFoto = async () => {
            if (!fotoDataUrl.value) return

            loading.value = true
            try {
                // Da dataURL → Blob → File
                let res = await fetch(fotoDataUrl.value)
                let blob = await res.blob()
                let file = new File([blob], nomeFileFoto.value, { type: 'image/jpeg' })

                await carica_allegato(props.ordineId, file)
                let response = await elenco_allegati(props.ordineId);
                allegatiInterni.value = response.data;
                emit('allegati-aggiornati', allegatiInterni.value)

                chiudiCamera()
            } catch (error) {
                console.error('Errore caricamento foto:', error)
            }
            loading.value = false
        }

        // ── Azioni allegati ──
        const scaricaAllegato = async (allegato) => {
            let res = await scarica_allegato(allegato.ordineId, allegato.id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', allegato.nomeFile);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }

        const eliminaAllegato = async (allegato) => {
            if (!confirm('Sei sicuro di voler eliminare questo allegato?')) return
            loading.value = true
            try {
                await elimina_allegato(props.ordineId, allegato.id)
                allegatiInterni.value = allegatiInterni.value.filter(a => a.id !== allegato.id)
                emit('allegati-aggiornati', allegatiInterni.value)
            } catch (error) {
                console.error('Errore eliminazione allegato:', error)
            }
            loading.value = false
        }

        // ── Utilità display ──
        const iconFile = (nome) => {
            const ext = (nome || '').split('.').pop().toLowerCase()
            const map = {
                pdf: 'mdi-file-pdf-box', png: 'mdi-file-image', jpg: 'mdi-file-image',
                jpeg: 'mdi-file-image', gif: 'mdi-file-image', doc: 'mdi-file-word',
                docx: 'mdi-file-word', xls: 'mdi-file-excel', xlsx: 'mdi-file-excel',
                zip: 'mdi-file-zip', rar: 'mdi-file-zip'
            }
            return map[ext] || 'mdi-file-outline'
        }

        const iconColor = (nome) => {
            const ext = (nome || '').split('.').pop().toLowerCase()
            const map = {
                pdf: '#e53935', png: '#43a047', jpg: '#43a047', jpeg: '#43a047', gif: '#43a047',
                doc: '#1e88e5', docx: '#1e88e5', xls: '#00897b', xlsx: '#00897b',
                zip: '#fb8c00', rar: '#fb8c00'
            }
            return map[ext] || '#757575'
        }

        const formatSize = (bytes) => {
            if (!bytes) return ''
            if (bytes < 1024) return bytes + ' B'
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
            return (bytes / 1048576).toFixed(1) + ' MB'
        }

        return {
            loading, fileInput, allegatiInterni, isDragOver,
            triggerFileInput, onFileChange, onDragOver, onDragLeave, onDrop,
            scaricaAllegato, eliminaAllegato, iconFile, iconColor, formatSize,
            // camera
            cameraAperta, videoEl, canvasEl, fotoAcquisita, fotoDataUrl,
            nomeFileFoto, cameraError, streamAttivo, dispositivi, dispositivoSelezionato,
            apriCamera, chiudiCamera, cambiaCamera, scattaFoto, riscatta, confermaFoto
        }
    }
}
</script>

<style scoped>
.comp-allegati {
    max-width: 960px;
    margin: 0 auto;
}

.comp-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    border-radius: 8px 8px 0 0;
}

/* ── Contenitore pulsanti affiancati ── */
.action-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

/* ── Upload zone ── */
.upload-zone {
    border: 1.5px dashed rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    background: #fafafa;
    text-align: center;
    padding: 20px 12px 18px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.upload-zone:hover {
    border-color: rgba(var(--v-theme-primary), 0.5);
    background: #f0f4ff;
}

.upload-zone.drag-over {
    border-color: rgb(var(--v-theme-primary));
    background: #e8eeff;
}

.upload-icon {
    color: rgba(0, 0, 0, 0.3);
}

/* ── Camera zone ── */
.camera-zone {
    border: 1.5px dashed rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    background: #fafafa;
    text-align: center;
    padding: 20px 12px 18px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.camera-zone:hover {
    border-color: rgba(76, 175, 80, 0.6);
    background: #f0fff1;
}

.camera-zone.camera-active {
    border-color: #43a047;
    background: #e8f5e9;
}

.camera-icon {
    color: rgba(0, 0, 0, 0.3);
}

.camera-label {
    color: #43a047;
}

/* ── Modale camera ── */
.camera-dialog {
    border-radius: 12px !important;
    overflow: hidden;
}

.video-container {
    position: relative;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    background: #000;
    aspect-ratio: 4/3;
}

.camera-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* Mirino a cornici d'angolo */
.viewfinder-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.corner {
    position: absolute;
    width: 24px;
    height: 24px;
    border-color: rgba(255, 255, 255, 0.8);
    border-style: solid;
}

.corner.tl {
    top: 12px;
    left: 12px;
    border-width: 2px 0 0 2px;
}

.corner.tr {
    top: 12px;
    right: 12px;
    border-width: 2px 2px 0 0;
}

.corner.bl {
    bottom: 12px;
    left: 12px;
    border-width: 0 0 2px 2px;
}

.corner.br {
    bottom: 12px;
    right: 12px;
    border-width: 0 2px 2px 0;
}

.camera-error-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 16px;
    text-align: center;
}

/* Anteprima foto scattata */
.preview-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 4/3;
}

.foto-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.preview-label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 11px;
    padding: 4px 10px;
    display: flex;
    align-items: center;
}

.camera-select :deep(.v-field) {
    font-size: 12px;
}

/* ── Lista allegati ── */
.allegati-list {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    overflow: hidden;
}

.allegato-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding: 6px 10px !important;
    min-height: 44px !important;
}

.allegato-item:last-child {
    border-bottom: none;
}

.allegato-item:hover {
    background: #f7f7f8;
}

.avatar-file {
    flex-shrink: 0;
}

.item-actions {
    display: flex;
    gap: 2px;
}
</style>